import { router } from "@inertiajs/react"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { ExpenseDialog } from "@/components/expense-dialog"
import { LaborCostPanel } from "@/pages/Projects/tabs/LaborCostPanel"
import { Section } from "@/components/steel"
import { formatCurrency, shortMoney } from "@/lib/utils"
import type { Expense } from "@/pages/Projects/tabs/types"

const BASE_TAG_SUGGESTIONS = ["reimbursable", "change-order", "warranty", "rework"]

export function ExpensesTab({
  expenses,
  initialBudget,
  projectId,
}: {
  expenses: Expense[]
  initialBudget: number
  projectId: string
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Expense | null>(null)

  const total = expenses.reduce((sum, e) => sum + e.amount, 0)
  const sorted = useMemo(
    () => [...expenses].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [expenses],
  )
  const tagSuggestions = useMemo(() => {
    const set = new Set(BASE_TAG_SUGGESTIONS)
    expenses.forEach((e) => (e.tags ?? []).forEach((t) => set.add(t)))
    return [...set]
  }, [expenses])

  return (
    <Section className="pb-7">
      <div className="mb-3.5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-title font-semibold">Expenses</h2>
          <p className="mt-0.5 text-meta text-muted-foreground">
            {expenses.length} {expenses.length === 1 ? "entry" : "entries"} · {formatCurrency(total)} recorded ·{" "}
            {shortMoney(initialBudget - total)} of budget left
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus />
          Add expense
        </Button>
      </div>

      {expenses.length === 0 ? (
        <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
          No expenses recorded yet.
        </p>
      ) : (
        <DataTable>
          <Thead>
            <Th>Date</Th>
            <Th>Description</Th>
            <Th>Category</Th>
            <Th>Vendor</Th>
            <Th align="center">Billable</Th>
            <Th align="right">Amount</Th>
            <Th />
          </Thead>
          <tbody>
            {sorted.map((expense) => (
              <Tr key={expense.id}>
                <Td muted num>
                  {expense.date}
                </Td>
                <Td strong>{expense.description || expense.category}</Td>
                <Td muted className="capitalize">
                  {expense.category}
                </Td>
                <Td muted>{expense.vendor}</Td>
                <Td align="center" muted className="text-label">
                  {expense.billable ? "Billable" : "Internal"}
                </Td>
                <Td align="right" num strong>
                  {formatCurrency(expense.amount)}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => {
                      setEditing(expense)
                      setDialogOpen(true)
                    }}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => router.delete(`/expenses/${expense.dbId}`, { preserveScroll: true })}
                  >
                    <Trash2 />
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <LaborCostPanel projectId={projectId} />

      <ExpenseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        projectId={projectId}
        editingExpense={editing}
        tagSuggestions={tagSuggestions}
      />
    </Section>
  )
}
